import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  MasterColumnDef,
  MasterScreenDefinition,
} from '../../models/masterDefinition.types';

/**
 * Which columns a master is showing.
 *
 * The selection is deliberately per-master and ephemeral: the reference
 * discards it when the active master changes, and nothing in the design
 * restores it across visits. Persisting it would leave a stale column set on a
 * shared machine with no affordance explaining why the table looks wrong.
 *
 * **Code and Name are always shown.** They are what identifies a row -- every
 * master keys them `code` and `name` -- so the chooser offers them ticked and
 * locked, and no selection, default or toggle can take them out of the table.
 */

/** The columns no one may hide: the record's Code and Name, on every master. */
export const LOCKED_COLUMN_KEYS: readonly string[] = ['code', 'name'];

export interface MasterColumnsResult {
  /** Every column the chooser offers, in display order. */
  available: readonly MasterColumnDef[];
  /** The visible ones, in the same order -- what the table renders. */
  visible: readonly MasterColumnDef[];
  isVisible: (key: string) => boolean;
  /**
   * Code and Name: shown, ticked and not toggleable. Optional because the chooser is shared with screens
   * that are not masters (Templates), which lock nothing.
   */
  isLocked?: (key: string) => boolean;
  toggle: (key: string) => void;
  /** Back to the definition's defaults. */
  restoreDefaults: () => void;
}

const isLocked = (key: string): boolean => LOCKED_COLUMN_KEYS.includes(key);

export function useMasterColumns(
  definition: MasterScreenDefinition,
): MasterColumnsResult {
  const defaults = useMemo(
    () => new Set<string>([...LOCKED_COLUMN_KEYS, ...definition.defaultColumns]),
    [definition.defaultColumns],
  );

  const [selected, setSelected] = useState<ReadonlySet<string>>(defaults);

  /*
    A different master means a different column set entirely -- carrying the
    previous selection over would hide columns the new master does have and
    show keys it does not.
  */
  useEffect(() => {
    setSelected(defaults);
  }, [defaults]);

  const isVisible = useCallback(
    (key: string) => isLocked(key) || selected.has(key),
    [selected],
  );

  const toggle = useCallback((key: string) => {
    if (isLocked(key)) return;
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const restoreDefaults = useCallback(() => {
    setSelected(defaults);
  }, [defaults]);

  const visible = useMemo(
    () => definition.columns.filter((column) => isLocked(column.key) || selected.has(column.key)),
    [definition.columns, selected],
  );

  return {
    available: definition.columns,
    visible,
    isVisible,
    isLocked,
    toggle,
    restoreDefaults,
  };
}